import { useCallback, useMemo } from 'react';
import { t } from '../strings';
import { digitsOf, validatePhone, validateRoutingNumber, validateTaxId, type ValidationOutcome } from '../validators';

/**
 * Formatted Input (piece 3): typing a known shape, never hiding one. The state
 * is the raw digit string, which is also the submitted value; the mask is a
 * display and input-assist layer. `X` is a digit placeholder and every other
 * character is a literal the field inserts itself.
 */
export const MASKS = {
  ein: 'XX-XXXXXXX',
  ssn: 'XXX-XX-XXXX',
  phone: '(XXX) XXX-XXXX',
  routing: 'XXXXXXXXX',
} as const;

export type MaskName = keyof typeof MASKS;

export function digitCapacity(mask: string): number {
  let n = 0;
  for (const c of mask) if (c === 'X') n += 1;
  return n;
}

export { digitsOf };

/** Lay the digits into the mask, inserting a literal only when a digit follows it, so nothing dangles. */
export function applyMask(mask: string, digits: string): string {
  let out = '';
  let i = 0;
  for (const c of mask) {
    if (i >= digits.length) break;
    if (c === 'X') {
      out += digits[i];
      i += 1;
    } else {
      out += c;
    }
  }
  return out;
}

/** A digit within capacity appends; anything else is ignored rather than accepted and flagged later. */
export function acceptKeystroke(mask: string, digits: string, key: string): string {
  if (!/^\d$/.test(key)) return digits;
  if (digits.length >= digitCapacity(mask)) return digits;
  return digits + key;
}

/** Backspace removes the last digit; literals are never part of the state, so none can be left dangling. */
export function backspaceDigit(digits: string): string {
  return digits.slice(0, -1);
}

/** Paste strips every non-digit first and applies the mask fresh. */
export function pasteDigits(mask: string, text: string): string {
  return digitsOf(text).slice(0, digitCapacity(mask));
}

export interface FormattedInputOptions {
  mask: string;
  /** The raw digit string, which is the submitted value. */
  value: string;
  onChange: (digits: string) => void;
}

export function useFormattedInput({ mask, value, onChange }: FormattedInputOptions) {
  const display = useMemo(() => applyMask(mask, value), [mask, value]);
  /** Returns true when the key was consumed (a digit, or Backspace) or must be swallowed (any other printable key). */
  const typeKey = useCallback(
    (key: string): boolean => {
      if (key === 'Backspace') {
        onChange(backspaceDigit(value));
        return true;
      }
      if (/^\d$/.test(key)) {
        onChange(acceptKeystroke(mask, value, key));
        return true;
      }
      return key.length === 1;
    },
    [mask, value, onChange],
  );
  const pasteText = useCallback((text: string) => onChange(pasteDigits(mask, text)), [mask, onChange]);
  return { display, typeKey, pasteText, capacity: digitCapacity(mask), complete: value.length === digitCapacity(mask) };
}

/**
 * The validator a Formatted Input runs against its raw digits (piece 3): the
 * mask's digit count first, then the Field Format Validator for the kind, the
 * phone, EIN and routing rules of piece 2. The SSN's only rule is nine digits
 * until a validator is declared for it. Every rule is US today and passes
 * elsewhere, as the validators do.
 */
export function formattedValidator(mask: string, countryCode = 'US'): (raw: string) => string | null {
  const capacity = digitCapacity(mask);
  const rule: ((value: string, countryCode: string) => ValidationOutcome) | null =
    mask === MASKS.phone ? validatePhone : mask === MASKS.ein ? validateTaxId : mask === MASKS.routing ? validateRoutingNumber : null;
  return (raw) => {
    const digits = digitsOf(raw);
    if (digits.length === 0) return null;
    if (digits.length < capacity) return `${t('field.enterAll')} ${capacity} ${t('field.digits')}`;
    if (!rule) return null;
    const outcome = rule(digits, countryCode);
    return outcome.ok ? null : outcome.message;
  };
}
